import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeads } from '@/hooks/useLeads';
import { useChatColorsStore } from '@/hooks/useChatColorsStore';
import type { LeadData } from '@/types/chat';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  Cell, PieChart, Pie, CartesianGrid, Legend 
} from 'recharts';
import * as XLSX from 'xlsx';
import { Download, LayoutDashboard, Users, Palette, Code2, Trash2, ArrowLeft, Store, ShoppingBag } from 'lucide-react';

const PRESET_COLORS = [
  { label: 'Rosa', value: '#E91E8C' },
  { label: 'Vermelho', value: '#F43F5E' },
  { label: 'Coral', value: '#FF6B6B' },
  { label: 'Laranja', value: '#F97316' },
  { label: 'Âmbar', value: '#F59E0B' },
  { label: 'Verde', value: '#10B981' },
  { label: 'Ciano', value: '#14B8A6' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Componentes Auxiliares
function StatCard({ label, value, icon: Icon, colorClass, subValue }: any) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-pink-50 shadow-sm flex items-center gap-4">
      <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${colorClass} text-white`}>
        <Icon size={24} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
        {subValue && <p className="text-xs text-green-500 font-medium">{subValue}</p>}
      </div>
    </div>
  );
}

function generateEmbedCode(primaryColor: string, webhookUrl: string): string {
  const c = primaryColor;
  const wh = webhookUrl || '';
  return `<script>
(function(){
  var PRIMARY='${c}',WEBHOOK='${wh}',STORAGE_KEY='bozzetti_leads';
  var s=document.createElement('style');
  s.textContent='.bz-w{position:fixed;bottom:24px;right:24px;z-index:9999;font-family:Poppins,Inter,sans-serif}'
  +'.bz-bubble{width:60px;height:60px;border-radius:50%;background:${c};border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 20px rgba(0,0,0,.2);transition:transform .2s}'
  +'.bz-bubble:hover{transform:scale(1.08)}'
  +'.bz-bubble img{width:36px;height:36px;object-fit:contain}'
  +'.bz-panel{position:absolute;bottom:72px;right:0;width:360px;max-height:560px;background:#fff;border-radius:20px;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 8px 40px rgba(0,0,0,.18);transition:opacity .25s,transform .25s;transform-origin:bottom right}'
  +'.bz-panel.bz-hide{opacity:0;transform:scale(.92);pointer-events:none}'
  +'.bz-head{background:${c};padding:14px 16px;display:flex;align-items:center;justify-content:space-between}'
  +'.bz-head-l{display:flex;align-items:center;gap:10px}'
  +'.bz-av{width:36px;height:36px;border-radius:50%;border:2px solid rgba(255,255,255,.5);object-fit:contain;background:rgba(255,255,255,.2)}'
  +'.bz-hn{color:#fff;font-weight:700;font-size:14px}'
  +'.bz-hs{color:rgba(255,255,255,.8);font-size:11px}'
  +'.bz-x{background:rgba(255,255,255,.2);border:none;border-radius:50%;width:28px;height:28px;cursor:pointer;color:#fff;font-size:15px;display:flex;align-items:center;justify-content:center}'
  +'.bz-msgs{flex:1;overflow-y:auto;padding:12px;display:flex;flex-direction:column;gap:8px;background:#FDF6F0}'
  +'.bz-m{max-width:82%;padding:10px 14px;border-radius:18px;font-size:13px;line-height:1.5;white-space:pre-line}'
  +'.bz-bot{background:#fff;color:#333;align-self:flex-start;border-bottom-left-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,.06)}'
  +'.bz-usr{background:${c};color:#fff;align-self:flex-end;border-bottom-right-radius:4px}'
  +'.bz-btns{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}'
  +'.bz-btn{padding:8px 16px;border-radius:999px;border:none;cursor:pointer;font-size:12px;font-weight:600;font-family:inherit;transition:opacity .15s;text-decoration:none;display:inline-block}'
  +'.bz-btn:hover{opacity:.85}'
  +'.bz-p{background:${c};color:#fff}'
  +'.bz-ml{background:#FFE600;color:#333}'
  +'.bz-sh{background:#EE4D2D;color:#fff}'
  +'.bz-wa{background:#25D366;color:#fff}'
  +'.bz-ig{background:linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888);color:#fff}'
  +'.bz-used{opacity:.4;pointer-events:none}'
  +'.bz-typing{display:flex;align-items:center;gap:4px;padding:10px 14px;background:#fff;border-radius:18px;border-bottom-left-radius:4px;align-self:flex-start;box-shadow:0 1px 4px rgba(0,0,0,.06)}'
  +'.bz-dot{width:7px;height:7px;border-radius:50%;background:#ccc;animation:bz-b 1.2s infinite}'
  +'.bz-dot:nth-child(2){animation-delay:.2s}.bz-dot:nth-child(3){animation-delay:.4s}'
  +'@keyframes bz-b{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}'
  +'.bz-inp-wrap{display:flex;gap:8px;padding:10px 12px;background:#fff;border-top:1px solid #f0f0f0}'
  +'.bz-inp{flex:1;border:1.5px solid #e5e7eb;border-radius:999px;padding:9px 16px;font-size:13px;font-family:inherit;outline:none}'
  +'.bz-inp:focus{border-color:${c}}'
  +'.bz-inp:disabled{background:#f9f9f9;cursor:not-allowed}'
  +'.bz-snd{width:36px;height:36px;border-radius:50%;background:${c};border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0}'
  +'.bz-snd svg{width:16px;height:16px;fill:none;stroke:#fff;stroke-width:2}'
  +'@media(max-width:400px){.bz-panel{width:calc(100vw - 24px);right:-12px}}';
  document.head.appendChild(s);
  var w=document.createElement('div');
  w.className='bz-w';
  w.innerHTML='<div class="bz-panel bz-hide" id="bz-pn">'
  +'<div class="bz-head"><div class="bz-head-l">'
  +'<img class="bz-av" src="https://static.readdy.ai/image/22602f61db723d886df2ffcff534fda8/4728923fdc166275b7926e4817bf6e64.png" alt="Bozzetti">'
  +'<div><div class="bz-hn">Bozzetti Store</div><div class="bz-hs">&#9679; Online</div></div>'
  +'</div><button class="bz-x" id="bz-x">&#x2715;</button></div>'
  +'<div class="bz-msgs" id="bz-ms"></div>'
  +'<div class="bz-inp-wrap"><input class="bz-inp" id="bz-in" type="text" placeholder="Digite sua mensagem..." autocomplete="off">'
  +'<button class="bz-snd" id="bz-sd"><svg viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg></button>'
  +'</div></div>'
  +'<button class="bz-bubble" id="bz-bb">'
  +'<img src="https://static.readdy.ai/image/22602f61db723d886df2ffcff534fda8/4728923fdc166275b7926e4817bf6e64.png" alt="Chat"></button>';
  document.body.appendChild(w);
  var pn=document.getElementById('bz-pn'),ms=document.getElementById('bz-ms');
  var inp=document.getElementById('bz-in'),snd=document.getElementById('bz-sd');
  var step='WAITING_NAME',nm='',em='',ph='',busy=false;
  function scr(){ms.scrollTop=ms.scrollHeight;}
  function bot(txt,btns,disInp){
    var el=document.createElement('div');
    el.className='bz-m bz-bot';
    el.textContent=txt;
    if(btns&&btns.length){
      var bd=document.createElement('div');
      bd.className='bz-btns';
      btns.forEach(function(b){
        var btn=b.link?document.createElement('a'):document.createElement('button');
        btn.className='bz-btn bz-'+b.v;
        btn.textContent=b.lbl;
        if(b.link){btn.href=b.href;btn.target='_blank';btn.rel='noopener';}
        else{btn.addEventListener('click',function(){
          bd.querySelectorAll('.bz-btn').forEach(function(x){x.classList.add('bz-used');});
          onFlow(b.val);
        });}
        bd.appendChild(btn);
      });
      el.appendChild(bd);
    }
    ms.appendChild(el);
    if(disInp){inp.disabled=true;}
    scr();
  }
  function usr(t){var el=document.createElement('div');el.className='bz-m bz-usr';el.textContent=t;ms.appendChild(el);scr();}
  function showT(){var el=document.createElement('div');el.className='bz-typing';el.id='bz-ty';el.innerHTML='<div class="bz-dot"></div><div class="bz-dot"></div><div class="bz-dot"></div>';ms.appendChild(el);scr();}
  function hideT(){var t=document.getElementById('bz-ty');if(t)t.remove();}
  function queue(items,d){
    var delay=d||0;
    items.forEach(function(it){
      delay+=600;
      var cap=delay;
      setTimeout(function(){
        showT();
        setTimeout(function(){
          hideT();
          bot(it.t,it.b,it.di);
          if(it.st)step=it.st;
        },900);
      },cap);
    });
  }
  function saveLead(){
    var lead={name:nm,email:em,phone:ph,interest:step==='tmp_v'?'Varejo':'Atacado'};
    if(WEBHOOK){try{fetch(WEBHOOK,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(lead)}).catch(function(){});}catch(e){}}
  }
  function start(){
    step='WAITING_NAME';nm='';em='';ph='';busy=false;
    inp.disabled=false;ms.innerHTML='';
    queue([{t:'Olá, seja bem-vinda à Bozzetti Store.\\nQual o seu nome?',st:'WAITING_NAME'}],0);
  }
  function onText(t){
    if(!t.trim()||inp.disabled)return;
    if(step==='WAITING_NAME'){nm=t;usr(t);queue([{t:'Qual o seu e-mail?',st:'WAITING_EMAIL'}]);}
    else if(step==='WAITING_EMAIL'){em=t;usr(t);queue([{t:'Qual o seu telefone?',st:'WAITING_PHONE'}]);}
    else if(step==='WAITING_PHONE'){ph=t;usr(t);inp.disabled=true;
      queue([{t:'Você tem interesse nas peças no atacado ou no varejo?',st:'WAITING_INTEREST',b:[{lbl:'Atacado',val:'Atacado',v:'p'},{lbl:'Varejo',val:'Varejo',v:'p'}]}]);
    }
  }
  function onFlow(val){
    if(step==='WAITING_INTEREST'){
      usr(val);
      var isV=val==='Varejo';
      saveLead();
      if(isV){queue([
        {t:'Para compras no varejo, acesse nossas lojas oficiais no Mercado Livre ou na Shopee.',b:[{lbl:'Mercado Livre',link:true,href:'https://www.mercadolivre.com.br/loja/bozzetti-store?utm_medium=social&utm_source=heylink.me',v:'ml'},{lbl:'Shopee',link:true,href:'https://shopee.com.br/bozzettistore702?categoryId=100017&entryPoint=ShopByPDP&itemId=58207761624&utm_medium=social&utm_source=heylink.me',v:'sh'}]},
        {t:'Obrigada pelo seu contato \\uD83D\\uDC96\\nNão se esqueça de seguir a nossa página no Instagram!',b:[{lbl:'Seguir no Instagram',link:true,href:'https://www.instagram.com/bozzettistore/',v:'ig'}]},
        {t:'Deseja iniciar uma nova conversa?',st:'RESTART_PROMPT',b:[{lbl:'Sim',val:'sim',v:'p'},{lbl:'Não',val:'nao',v:'p'}],di:true}
      ]);}else{queue([
        {t:'O pedido mínimo para o atacado é de 10 peças, podendo ser peças variadas. Realizamos vendas para CPF ou CNPJ e envio para todo o Brasil. Para vendas no atacado, entre em contato conosco através do WhatsApp 11974876232. Ou clique no botão abaixo.',b:[{lbl:'Falar no WhatsApp',link:true,href:'https://wa.me/5511974876232',v:'wa'}]},
        {t:'Obrigada pelo seu contato \\uD83D\\uDC96\\nNão se esqueça de seguir a nossa página no Instagram!',b:[{lbl:'Seguir no Instagram',link:true,href:'https://www.instagram.com/bozzettistore/',v:'ig'}]},
        {t:'Deseja iniciar uma nova conversa?',st:'RESTART_PROMPT',b:[{lbl:'Sim',val:'sim',v:'p'},{lbl:'Não',val:'nao',v:'p'}],di:true}
      ]);}
    }else if(step==='RESTART_PROMPT'){
      usr(val==='sim'?'Sim':'Não');
      if(val==='sim'){setTimeout(start,400);}
      else{queue([{t:'Foi um prazer te atender! \\uD83D\\uDC96',st:'END',di:true}]);}
    }
  }
  document.getElementById('bz-bb').addEventListener('click',function(){
    pn.classList.toggle('bz-hide');
    if(!pn.classList.contains('bz-hide')&&ms.children.length===0)start();
  });
  document.getElementById('bz-x').addEventListener('click',function(){pn.classList.add('bz-hide');});
  snd.addEventListener('click',function(){var t=inp.value.trim();if(t){inp.value='';onText(t);}});
  inp.addEventListener('keydown',function(e){if(e.key==='Enter'){var t=inp.value.trim();if(t){inp.value='';onText(t);}}});
})();
<\/script>`;
}

type TabType = 'analytics' | 'leads' | 'personalizar' | 'incorporar';

export default function Dashboard() {
  const navigate = useNavigate();
  const { leads, clearLeads, loadLeads } = useLeads();
  const { colors, updateColors, resetColors } = useChatColorsStore();
  
  const [activeTab, setActiveTab] = useState<TabType>('analytics');
  const [filter, setFilter] = useState<'all' | 'Atacado' | 'Varejo'>('all');
  const [showConfirm, setShowConfirm] = useState(false);
  const [customColor, setCustomColor] = useState(colors.primary);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Cálculos Memoized para Gráficos
  const analyticsData = useMemo(() => {
    const total = leads.length;
    const atacado = leads.filter(l => l.interest === 'Atacado').length;
    const varejo = leads.filter(l => l.interest === 'Varejo').length;
    
    // Agrupar por data (últimos 7 dias)
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      const count = leads.filter(l => new Date(l.createdAt).toLocaleDateString('pt-BR').startsWith(dateStr.split('/')[0])).length;
      return { name: dateStr, total: count };
    }).reverse();

    const pieData = [
      { name: 'Atacado', value: atacado, color: '#6366f1' },
      { name: 'Varejo', value: varejo, color: '#ec4899' }
    ];

    return { total, atacado, varejo, last7Days, pieData };
  }, [leads]);

  const filtered = filter === 'all' ? leads : leads.filter((l) => l.interest === filter);

  // Exportação Excel
  const handleExport = () => {
    const dataToExport = leads.map(l => ({
      Nome: l.name,
      Email: l.email,
      Telefone: l.phone,
      Interesse: l.interest,
      Data: formatDate(l.createdAt)
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    XLSX.writeFile(wb, `Leads_Bozzetti_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const applyColor = (hex: string) => {
    setCustomColor(hex);
    updateColors({ primary: hex });
  };

  const embedCode = generateEmbedCode(colors.primary, webhookUrl);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }, [embedCode]);

  const tabs: { id: TabType; icon: any; label: string }[] = [
    { id: 'analytics', icon: LayoutDashboard, label: 'Analytics' },
    { id: 'leads', icon: Users, label: 'Leads' },
    { id: 'personalizar', icon: Palette, label: 'Personalizar' },
    { id: 'incorporar', icon: Code2, label: 'Incorporar' },
  ];

  return (
    <div className="min-h-screen px-4 py-8" style={{ background: 'linear-gradient(135deg,#FDF6F0 0%,#FAF0F4 50%,#FDF2F8 100%)', fontFamily: 'Poppins,Inter,sans-serif' }}>
      <div className="max-w-6xl mx-auto">
        
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-pink-200 text-pink-500 hover:bg-pink-50 shadow-sm transition-all cursor-pointer">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Painel de Controle</h1>
              <p className="text-sm text-gray-500">Bozzetti Store Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition-all shadow-md cursor-pointer">
              <Download size={16} /> Exportar Excel
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard label="Total de Leads" value={analyticsData.total} icon={Users} colorClass="bg-gradient-to-br from-pink-400 to-rose-500" />
          <StatCard label="Interesse Atacado" value={analyticsData.atacado} icon={Store} colorClass="bg-gradient-to-br from-indigo-500 to-indigo-700" />
          <StatCard label="Interesse Varejo" value={analyticsData.varejo} icon={ShoppingBag} colorClass="bg-gradient-to-br from-pink-400 to-pink-600" />
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-gray-200/50 p-1 rounded-2xl mb-8 w-fit border border-white/50 backdrop-blur-sm">
          {tabs.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${activeTab === id ? 'bg-white text-pink-600 shadow-md scale-[1.02]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Tab: Analytics (NOVO) ── */}
        {activeTab === 'analytics' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Gráfico de Barras - Leads por Dia */}
            <div className="bg-white p-6 rounded-3xl border border-pink-50 shadow-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-6">Volume de Leads (7 dias)</h3>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData.last7Days}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                    <Tooltip cursor={{fill: '#fef2f2'}} contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}} />
                    <Bar dataKey="total" fill="#ec4899" radius={[6, 6, 0, 0]} barSize={35} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Gráfico de Pizza - Distribuição */}
            <div className="bg-white p-6 rounded-3xl border border-pink-50 shadow-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-6">Distribuição de Perfil</h3>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData.pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={8}
                      dataKey="value"
                    >
                      {analyticsData.pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{borderRadius: '12px', border: 'none'}} />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Leads (Lista) ── */}
        {activeTab === 'leads' && (
          <div className="bg-white rounded-3xl border border-pink-50 shadow-sm overflow-hidden animate-in fade-in duration-500">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-5 border-b border-gray-50 bg-gray-50/30">
              <div className="flex gap-2">
                {(['all', 'Atacado', 'Varejo'] as const).map((f) => (
                  <button key={f} onClick={() => setFilter(f)}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer ${filter === f ? 'bg-pink-500 text-white shadow-md' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}>
                    {f === 'all' ? 'Todos' : f}
                  </button>
                ))}
              </div>
              {leads.length > 0 && (
                <button onClick={() => setShowConfirm(true)}
                  className="flex items-center gap-2 text-xs text-red-500 hover:text-red-700 font-bold cursor-pointer px-4 py-2 rounded-xl border border-red-100 hover:bg-red-50 transition-all">
                  <Trash2 size={14} /> Limpar Banco de Dados
                </button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] text-gray-400 uppercase tracking-[0.1em] bg-gray-50/50">
                    <th className="py-4 px-6 font-bold">Cliente</th>
                    <th className="py-4 px-6 font-bold">Contato</th>
                    <th className="py-4 px-6 font-bold">Interesse</th>
                    <th className="py-4 px-6 font-bold">Data/Hora</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((lead) => (
                    <tr key={lead.id} className="hover:bg-pink-50/30 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-bold text-sm">
                            {lead.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-semibold text-gray-700">{lead.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-600 font-medium">{lead.email}</div>
                        <div className="text-xs text-gray-400">{lead.phone}</div>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${lead.interest === 'Atacado' ? 'bg-indigo-50 text-indigo-600' : 'bg-pink-50 text-pink-600'}`}>
                          {lead.interest}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-xs text-gray-400 font-medium">{formatDate(lead.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="py-20 text-center">
                  <Users size={48} className="mx-auto text-pink-100 mb-4" />
                  <p className="text-gray-400 font-medium">Nenhum lead encontrado.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Tab: Personalizar ── */}
        {activeTab === 'personalizar' && (
          <div className="bg-white rounded-3xl border border-pink-50 p-8 shadow-sm animate-in fade-in duration-500">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Identidade Visual</h2>
            <p className="text-sm text-gray-500 mb-8">Personalize a cor mestre do seu chatbot para combinar com seu site.</p>
            
            <div className="flex flex-col md:flex-row gap-12">
              <div className="flex-1 space-y-8">
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Cores Sugeridas</p>
                  <div className="flex flex-wrap gap-4">
                    {PRESET_COLORS.map((p) => (
                      <button key={p.value} onClick={() => applyColor(p.value)} className="group flex flex-col items-center gap-2 cursor-pointer">
                        <div className="w-12 h-12 rounded-2xl shadow-sm transition-transform group-hover:scale-110" style={{ backgroundColor: p.value, border: customColor === p.value ? '3px solid #000' : 'none' }} />
                        <span className="text-[10px] font-bold text-gray-500">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Cor Manual (HEX)</p>
                  <div className="flex items-center gap-4">
                    <input type="color" value={customColor} onChange={(e) => applyColor(e.target.value)} className="w-14 h-14 rounded-xl cursor-pointer border-none" />
                    <input type="text" value={customColor} onChange={(e) => applyColor(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 font-mono text-sm outline-none focus:ring-2 focus:ring-pink-200" />
                  </div>
                </div>
              </div>

              <div className="w-full md:w-80 bg-gray-50 rounded-3xl p-6 border border-gray-100 flex flex-col items-center justify-center text-center">
                <p className="text-xs font-bold text-gray-400 uppercase mb-6">Prévia do Widget</p>
                <div className="w-20 h-20 rounded-full shadow-2xl flex items-center justify-center mb-4 transition-colors duration-500" style={{ backgroundColor: customColor }}>
                   <img src="https://static.readdy.ai/image/22602f61db723d886df2ffcff534fda8/4728923fdc166275b7926e4817bf6e64.png" className="w-12" alt="" />
                </div>
                <div className="px-6 py-2 rounded-full text-white text-xs font-bold shadow-md" style={{ backgroundColor: customColor }}>
                  Botão de Exemplo
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Incorporar ── */}
        {activeTab === 'incorporar' && (
          <div className="space-y-6 animate-in fade-in duration-500">
            <div className="bg-white rounded-3xl border border-pink-50 p-8 shadow-sm">
              <h2 className="text-xl font-bold text-gray-800 mb-2">Instalação no Site</h2>
              <p className="text-sm text-gray-500 leading-relaxed max-w-2xl">
                Para ativar o chatbot na sua loja, copie o código abaixo e cole no final do seu arquivo HTML, logo antes da tag <code className="bg-gray-100 px-2 py-0.5 rounded text-pink-600">&lt;/body&gt;</code>.
              </p>
            </div>

            <div className="bg-gray-900 rounded-3xl overflow-hidden shadow-2xl border border-gray-800">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-gray-800/50">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-2 px-6 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${copied ? 'bg-green-500 text-white' : 'bg-pink-600 text-white hover:bg-pink-500'}`}
                >
                  {copied ? 'Copiado!' : 'Copiar Script'}
                </button>
              </div>
              <div className="p-8">
                <pre className="text-xs text-pink-300 font-mono whitespace-pre-wrap break-all leading-relaxed h-[300px] overflow-y-auto">
                  {embedCode}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Confirmação */}
      {showConfirm && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl scale-in-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <Trash2 className="text-red-500" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">Apagar tudo?</h3>
            <p className="text-sm text-gray-500 mb-8 text-center">Isso removerá permanentemente todos os leads do seu banco de dados Firebase.</p>
            <div className="flex gap-4">
              <button onClick={() => setShowConfirm(false)} className="flex-1 px-6 py-3 bg-gray-100 text-gray-600 rounded-2xl text-sm font-bold hover:bg-gray-200 transition-all cursor-pointer">Cancelar</button>
              <button onClick={() => { clearLeads(); setShowConfirm(false); }} className="flex-1 px-6 py-3 bg-red-500 text-white rounded-2xl text-sm font-bold hover:bg-red-600 shadow-lg shadow-red-200 transition-all cursor-pointer">Apagar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}